import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { ReceptorRecord } from '@trustero/trustero-api-web/lib/agent/receptor_pb'
import { Timestamp } from 'google-protobuf/google/protobuf/timestamp_pb'
import { DOCUMENT_TYPE } from '@trustero/trustero-api-web/lib/attachment/attachment_pb'
import {
  SetLinkRequest,
  LinkDoc,
} from '@trustero/trustero-api-web/lib/linker/linker_pb'
import { LinkerPromiseClient } from '@trustero/trustero-api-web/lib/linker/linker_grpc_web_pb'
import { useAuthorizedGrpcClient } from 'src/adapter/grpcClient'
import { useUser } from 'src/components/async/Users'

import { useDocumentPickerIfAvailable } from 'src/components/DocumentPicker'
import type { DocumentNode } from 'src/components/DocumentPicker'
import { LINKER_MODEL_IDS, Source } from '../../Linkers.constants'
import type {
  ConfluenceNode,
  ConfluenceTree,
} from './ConfluenceFilePickerModal.types'

/**
 * A document picker node in the shape the Confluence linking flows read.
 * `spaceId` is the space key the picker reports, the first segment of the
 * `<spaceKey>:<pageId>:<type>` import key the receptor keys links on.
 */
export const confluenceNodeFromDocumentNode = (
  node: DocumentNode,
): ConfluenceNode => ({
  id: node.id,
  title: node.title,
  type:
    node.kind === 'container'
      ? 'space'
      : node.kind === 'label'
      ? 'label'
      : 'page',
  spaceId: node.spaceId,
  url: node.url,
  relevantDate: node.modifiedAt,
  fileName: node.title,
})

interface OnSubmitProps {
  caption: string
  node: ConfluenceNode
  /** Optional batch selection. If provided, `node` is ignored. */
  nodes?: ConfluenceNode[]
  isViaRequest?: boolean
  relevantDate?: Timestamp
}

export interface ConfluenceFilePickerContextValue {
  tree: ConfluenceTree | null
  setTree: React.Dispatch<React.SetStateAction<ConfluenceTree | null>>
  loading: boolean
  setLoading: (value: boolean) => void
  selectedNode: ConfluenceNode | null
  setSelectedNode: React.Dispatch<React.SetStateAction<ConfluenceNode | null>>
  /**
   * Files selected from Confluence, surfaced in the AddDocumentForm summary
   * (mirrors the SharePoint / Google Drive pattern).
   */
  filesSelected: ConfluenceNode[]
  setFilesSelected: React.Dispatch<React.SetStateAction<ConfluenceNode[]>>
  onSubmit: (props: OnSubmitProps) => Promise<string[]>
  source: Source
  setSource: (source: Source) => void
  subjectIds: string[]
  setSubjectIds: (ids: string[]) => void
  receptor?: ReceptorRecord
  /**
   * Opener API: the connection the user chose, set right before opening the
   * picker. Bumps `receptorRequestSeq` so the picker can tell a fresh request
   * from a value left behind by an earlier picker session.
   */
  setReceptor: (receptor: ReceptorRecord) => void
  /**
   * Picker API, called once when a picker opens: true when setReceptor was
   * called since any picker last opened, i.e. `receptor` is the opener's
   * choice rather than a value left behind by an earlier picker session.
   * Shared across every picker instance in the app.
   */
  consumeReceptorRequest: () => boolean
  /** Picker API: publishes the row it resolved, without marking a new request. */
  setResolvedReceptor: (receptor: ReceptorRecord) => void
}

const ConfluenceFilePickerContext = createContext<
  ConfluenceFilePickerContextValue | undefined
>(undefined)

export const ConfluenceFilePickerProvider: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  const [tree, setTree] = useState<ConfluenceTree | null>(null)
  const [loadingState, setLoadingState] = useState<boolean>(true)
  const [selectedNode, setSelectedNode] = useState<ConfluenceNode | null>(null)
  const [filesSelected, setFilesSelected] = useState<ConfluenceNode[]>([])

  const [source, setSource] = useState<Source>('Evidence')
  const [subjectIds, setSubjectIds] = useState<string[]>([])

  const setLoading = (value: boolean): void => {
    setLoadingState(value)
  }

  const [receptor, setResolvedReceptor] = useState<ReceptorRecord | undefined>(
    undefined,
  )
  // Request bookkeeping lives in refs: it is read during a picker's render
  // and must not itself cause renders.
  const receptorRequestSeqRef = useRef(0)
  const consumedReceptorRequestSeqRef = useRef(0)
  const setReceptor = useCallback((next: ReceptorRecord): void => {
    receptorRequestSeqRef.current += 1
    setResolvedReceptor(next)
  }, [])
  const consumeReceptorRequest = useCallback((): boolean => {
    const fresh =
      receptorRequestSeqRef.current !== consumedReceptorRequestSeqRef.current
    consumedReceptorRequestSeqRef.current = receptorRequestSeqRef.current
    return fresh
  }, [])

  const { data: user } = useUser()
  const userId = useMemo(() => user?.getId() || '', [user])

  const linkerClient = useAuthorizedGrpcClient(LinkerPromiseClient)

  // The document picker (accounts on NEW_DOCUMENT_PICKER) browses Confluence
  // as the receptor's identity and hands the picked pages over here, in the
  // shape the linking flows expect. Published as a resolved row, not a new
  // request, so a later opener-less open does not mistake it for a fresh
  // choice.
  const documentPicker = useDocumentPickerIfAvailable()
  const selection = documentPicker?.selection
  const clearSelection = documentPicker?.clearSelection
  useEffect(() => {
    if (!selection || !clearSelection || selection.source === 'FolderScan')
      return
    if (selection.receptor.getModelid() !== LINKER_MODEL_IDS.CONFLUENCE) return
    setResolvedReceptor(selection.receptor)
    setFilesSelected(selection.nodes.map(confluenceNodeFromDocumentNode))
    clearSelection()
  }, [selection, clearSelection])

  const onSubmit = async ({
    caption,
    node,
    nodes,
    relevantDate,
    isViaRequest = false,
  }: OnSubmitProps): Promise<string[]> => {
    if (!receptor) return []

    const receptorId = receptor.getId() || ''
    const request = new SetLinkRequest().setReceptorId(receptorId)

    const selectedNodes =
      Array.isArray(nodes) && nodes.length > 0 ? nodes : [node]

    const docs = selectedNodes.map((n) => {
      const nodeUrl = n.url ?? ''
      const nodeFileName = n.fileName || n.title || String(n.id)

      let ts: Timestamp | undefined
      const rawDate = (relevantDate ?? n.relevantDate) as string | undefined

      if (rawDate) {
        const parsed = new Date(rawDate)
        if (!Number.isNaN(parsed.getTime())) {
          ts = Timestamp.fromDate(parsed)
        }
      }

      return new LinkDoc()
        .setImportKey(`${n.spaceId}:${n.id}:${source}`)
        .setImportLink(nodeUrl)
        .setSubjectIdsList(subjectIds)
        .setDocumentType(
          source === 'Evidence'
            ? DOCUMENT_TYPE.EVIDENCE
            : DOCUMENT_TYPE.POLICYDOC,
        )
        .setRelevantDate(ts)
        .setIsViaRequest(isViaRequest)
        .setCaption(
          caption && caption.length > 0
            ? caption
            : n.title ?? String(n.fileName),
        )
        .setFileName(nodeFileName)
        .setReceptorModelId(receptor.getModelid())
        .setUserId(userId)
    })

    request.setDocumentsList(docs)

    setLoading(false)

    const response = await linkerClient.setLink(request)
    return response.getEvidenceIdsList()
  }

  return (
    <ConfluenceFilePickerContext.Provider
      value={{
        tree,
        setTree,
        loading: loadingState,
        setLoading,
        selectedNode,
        setSelectedNode,
        filesSelected,
        setFilesSelected,
        onSubmit,
        source,
        setSource,
        subjectIds,
        setSubjectIds,
        receptor,
        setReceptor,
        consumeReceptorRequest,
        setResolvedReceptor,
      }}
    >
      {children}
    </ConfluenceFilePickerContext.Provider>
  )
}

export function useConfluenceFilePickerContext(): ConfluenceFilePickerContextValue {
  const ctx = useContext(ConfluenceFilePickerContext)
  if (!ctx) {
    throw new Error(
      'useConfluenceFilePickerContext must be used within ConfluenceFilePickerProvider',
    )
  }
  return ctx
}
